import { useState } from "react";
import { useHistory } from "react-router-dom";
import { message, Modal } from "antd";
import { incidentService } from "@iso/services";
import { validateCorrectiveActions } from "./correctiveActionHelpers";

const BRAND_BUTTON_PROPS = {
  style: { backgroundColor: "#0B2A1B", borderColor: "#0B2A1B" },
};

/**
 * Shared handlers for investigation form pages (5 Whys and ICAM).
 *
 * @param {string|number} incidentId
 * @param {object} formData - The current form state (will be JSON.stringified for content)
 * @param {object|null} investigation - The loaded investigation record
 * @returns {{ saving, submitting, approving, handleSaveDraft, handleSubmit, handleApprove, handleRequestMoreInfo }}
 */
const useInvestigationHandlers = (incidentId, formData, investigation, reviewComment) => {
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      await incidentService.editInvestigation(incidentId, {
        content: JSON.stringify(formData),
      });
      message.success("Investigation saved as draft.");
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    Modal.confirm({
      title: "Submit Investigation",
      content:
        "Are you sure you want to submit this investigation for review? You can still edit it if more information is requested.",
      okButtonProps: BRAND_BUTTON_PROPS,
      onOk: async () => {
        try {
          setSubmitting(true);
          await incidentService.submitInvestigation(incidentId, {
            content: JSON.stringify(formData),
            evidence: investigation?.evidence || "",
            evidence_attachment_ids: (
              investigation?.evidence_attachments || []
            ).map((a) => a.id),
          });
          message.success("Investigation submitted for review.");
          history.push("/incidents");
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to submit investigation."
          );
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const handleApprove = (correctiveActions) => {
    const safeActions = Array.isArray(correctiveActions) ? correctiveActions : [];
    const validationError = validateCorrectiveActions(safeActions);
    if (validationError) {
      message.error(validationError);
      return;
    }

    // Send only the fields the backend needs to overlay onto stored content.
    const approverPayload = safeActions.map((ca) => ({
      id: ca?.id,
      assignedTo: ca?.assignedTo,
      dueDate: ca?.dueDate,
    }));

    Modal.confirm({
      title: "Approve Investigation",
      content:
        "Approve this investigation? Each listed corrective action will be assigned to the chosen person with the chosen due date, and they will be notified by email.",
      okButtonProps: BRAND_BUTTON_PROPS,
      onOk: async () => {
        try {
          setApproving(true);
          await incidentService.approveInvestigation(
            incidentId,
            reviewComment,
            approverPayload,
          );
          message.success("Investigation approved.");
          history.push("/incidents");
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to approve."
          );
        } finally {
          setApproving(false);
        }
      },
    });
  };

  const handleRequestMoreInfo = () => {
    Modal.confirm({
      title: "Request More Information",
      content:
        "Send this investigation back to the investigator for more details?",
      okButtonProps: BRAND_BUTTON_PROPS,
      onOk: async () => {
        try {
          setApproving(true);
          await incidentService.requestMoreInfo(incidentId, reviewComment);
          message.success("Request sent to investigator.");
          history.push("/incidents");
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to request more info."
          );
        } finally {
          setApproving(false);
        }
      },
    });
  };

  return {
    saving,
    submitting,
    approving,
    handleSaveDraft,
    handleSubmit,
    handleApprove,
    handleRequestMoreInfo,
  };
};

export default useInvestigationHandlers;
